import { EMPTY_VALUE } from '@bridge/utils';

import type { CurrencyId } from './data';

/**
 * Extracts the narrow currency symbol for a given ISO 4217 code.
 * Falls back to the uppercased code if `Intl.NumberFormat` doesn't recognise it.
 *
 * @example
 * getCurrencySymbol('usd') // '$'
 * getCurrencySymbol('eur') // '€'
 * getCurrencySymbol('gbp') // '£'
 */
export function getCurrencySymbol(currency: CurrencyId, locale = 'en-us'): string {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? currency.toUpperCase();
  } catch {
    return currency.toUpperCase();
  }
}

const DEFAULT_OPTIONS: Intl.NumberFormatOptions & { locale: string } = {
  locale: 'en-us',
  style: 'currency',
  currency: 'USD',
  currencyDisplay: 'narrowSymbol',
};

/**
 * Creates a `formatCurrency` function that formats amounts with a currency symbol,
 * using each currency's backing fiat for `Intl.NumberFormat` so stablecoins render
 * with their fiat symbol (e.g. `usdc` → `$1,234.56`).
 *
 * @template C - Currency type accepted by the formatter. Defaults to `CurrencyId`
 *   (the frontend's curated set). Apps whose data source has a broader currency
 *   enum (e.g. a backend OpenAPI `Currency` schema) can pin `C` to that union to
 *   avoid casting at call sites — unknown currencies fall back to the raw code.
 *
 * @example
 * const registry = createCurrencyRegistry(ALL_CURRENCY_CONFIG);
 * const formatCurrency = createFormatMoney((c) => registry.getBackingCurrency(c));
 * formatCurrency(1234.56, { currency: 'usdc' }); // '$1,234.56'
 * formatCurrency(NaN, { currency: 'usd' });      // '--'
 * formatCurrency(NaN, { currency: 'usd', includeCurrencyMarkOnNullAmount: true }); // '$-- '
 */
export function createFormatMoney<C extends string = CurrencyId>(
  getBackingCurrency: (currency: C) => string | undefined,
) {
  return function formatCurrency(
    amount: number,
    config: Partial<Intl.NumberFormatOptions> & {
      currency: C;
      includeCurrencyMarkOnNullAmount?: boolean;
      locale?: string;
      minimumFractionDigits?: number;
      maximumFractionDigits?: number;
    },
  ): string {
    const { includeCurrencyMarkOnNullAmount, locale, ...intlOptions } = { ...DEFAULT_OPTIONS, ...config };
    const backingCurrency = getBackingCurrency(config.currency) ?? config.currency;

    let formattedAmount: string;
    try {
      formattedAmount = new Intl.NumberFormat(locale, { ...intlOptions, currency: backingCurrency }).format(amount);
    } catch {
      // `Intl.NumberFormat` throws `RangeError` for currency codes that aren't
      // valid ISO 4217. Fall back to plain number formatting with the raw code appended.
      const { style: _style, currency: _currency, currencyDisplay: _display, ...plainOptions } = intlOptions;
      formattedAmount = `${new Intl.NumberFormat(locale, plainOptions).format(amount)} ${config.currency}`;
    }

    if (formattedAmount.includes('NaN')) {
      formattedAmount = includeCurrencyMarkOnNullAmount ? formattedAmount.replace('NaN', EMPTY_VALUE) : EMPTY_VALUE;
    }

    return formattedAmount;
  };
}

/**
 * Creates a function returning a currency's canonical decimal count via its backing fiat
 * (usd/eur → 2, jpy → 0), for padding an amount to a conventional width. It reads only the
 * digit count from `Intl` and never formats the amount, so it cannot round; the caller pads
 * the exact decimal string itself. Falls back to 2 for an unrecognized code.
 *
 * @example
 * const currencyFractionDigits = createCurrencyFractionDigits((c) => registry.getBackingCurrency(c));
 * formatDecimalString('3.4', currencyFractionDigits('usd')); // '3.40'
 */
export function createCurrencyFractionDigits(getBackingCurrency: (currency: string) => string | undefined) {
  return function currencyFractionDigits(currency: string, locale = 'en-us'): number {
    const backingCurrency = getBackingCurrency(currency) ?? currency;
    try {
      return (
        new Intl.NumberFormat(locale, { style: 'currency', currency: backingCurrency }).resolvedOptions()
          .maximumFractionDigits ?? 2
      );
    } catch {
      return 2;
    }
  };
}

/**
 * Formats a currency + payment rail pair.
 *
 * @returns EMPTY_VALUE when currency is absent; otherwise a formatted string such as "usd.circle" or "usd"
 */
export function formatCurrencyRail(
  currency: string | null | undefined,
  rail: string | null | undefined,
): string {
  if (currency == null) return EMPTY_VALUE;
  if (!rail) return currency;
  return `${currency}.${rail}`;
}
